import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import FavoriteButton from '../../components/FavoriteButton';
import ShareButton from '../../components/ShareButton';
import { getImageUrl } from '../../utils/imageUrl';
import { MapPin, ExternalLink, ArrowLeft, Star, Award, CheckCircle, Heart, Share2, Phone, Mail, Instagram, Globe, Users, Trophy, X, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '../../components/ui/carousel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProviderDetailPage = ({ user, onLogout }) => {
  const { providerId, slugWithId } = useParams();  // Support both old and new format
  const navigate = useNavigate();
  const location = useLocation();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Extract public_id from new URL format (/p/slug-publicId)
  const getPublicId = () => {
    if (slugWithId) {
      // New format: /p/slug-publicId
      // Extract last 16 characters as public_id
      const parts = slugWithId.split('-');
      const publicId = parts[parts.length - 1];
      return publicId;
    }
    // Old format: /provider/:providerId
    return providerId;
  };
  
  const actualProviderId = getPublicId();
  
  const [requestForm, setRequestForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    event_type: '',
    event_date: '',
    event_location: '',
    message: ''
  });
  const [showDialog, setShowDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [suggestedProviders, setSuggestedProviders] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average_rating: 0, total_reviews: 0 });
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [providerReferences, setProviderReferences] = useState([]);

  useEffect(() => {
    loadProvider();
    loadSuggestions();
    loadReviewStats();
    loadReviews();
    loadReferences();
  }, [actualProviderId]);

  const loadProvider = async () => {
    try {
      // Use public endpoint for public profiles
      const response = await axios.get(`${API}/public/provider/${actualProviderId}`);
      setProvider(response.data);
    } catch (error) {
      console.error('Error loading provider:', error);
      toast.error('Dienstleister nicht gefunden');
      navigate('/search');
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/provider/${actualProviderId}/suggestions`);
      setSuggestedProviders(response.data);
    } catch (error) {
      console.error('Error loading suggestions:', error);
      // Silent fail - suggestions are optional
    }
  };

  const loadReviewStats = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${actualProviderId}/stats`);
      setReviewStats(response.data);
    } catch (error) {
      console.error('Error loading review stats:', error);
      // Silent fail - will use defaults
    }
  };

  const loadReviews = async () => {
    try {
      setReviewsLoading(true);
      const response = await axios.get(`${API}/reviews/${actualProviderId}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  const loadReferences = async () => {
    try {
      const response = await axios.get(`${API}/provider/${actualProviderId}/references`);
      setProviderReferences(response.data);
    } catch (error) {
      console.error('Error loading references:', error);
      // Silent fail - references are optional
    }
  };

  // Name aus dem Konto vorbefuellen, damit der Kunde ihn nicht doppelt tippt.
  useEffect(() => {
    if (user && !requestForm.customer_name) {
      const preset = user.display_name || user.name || '';
      if (preset) setRequestForm((f) => ({ ...f, customer_name: preset }));
    }
  }, [user]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Anfragen erfordern ein Kundenkonto, damit der Chat eine identifizierbare
  // Gegenseite hat. Das Gate sitzt am Button (nicht im Dialog), damit nie ein
  // halb ausgefuelltes Formular verloren geht.
  const openInquiry = () => {
    if (!user) {
      toast.info('Bitte melde dich an, um eine Anfrage zu senden.');
      navigate('/login', { state: { from: location.pathname + location.search } });
      return;
    }
    if (user.role !== 'customer') {
      toast.error('Als Dienstleister kannst du keine Anfrage senden.');
      return;
    }
    setShowDialog(true);
  };

  const handleSendRequest = async (e) => {
    e.preventDefault();

    // Client-Validierung spiegelt die Server-Grenzen aus ServiceRequestCreate
    // (server.py). Ohne diese Checks quittiert der Server zu kurze Eingaben mit
    // einem 422, das der Nutzer sonst nur als generischen Fehler saehe.
    const name = requestForm.customer_name.trim();
    const eventType = requestForm.event_type.trim();
    const location = requestForm.event_location.trim();
    const message = requestForm.message.trim();

    if (name.length < 2) {
      toast.error('Bitte gib deinen Namen an (mindestens 2 Zeichen).');
      return;
    }
    if (eventType.length < 2) {
      toast.error('Bitte gib die Art des Events an.');
      return;
    }
    if (location.length < 2) {
      toast.error('Bitte gib den Ort des Events an.');
      return;
    }
    if (message.length < 10) {
      toast.error('Deine Nachricht muss mindestens 10 Zeichen lang sein.');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/requests`,
        {
          // provider.id ist die interne UUID; actualProviderId ist die public_id
          // aus der URL und wuerde serverseitig nicht aufgeloest werden.
          provider_id: provider.id,
          // customer_email kommt serverseitig aus dem Konto und ist damit nicht
          // faelschbar; der Name bleibt frei waehlbar (reine Anzeige).
          customer_name: name,
          customer_phone: requestForm.customer_phone,
          event_type: eventType,
          event_date: requestForm.event_date,
          event_location: location,
          message: message
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show a large, prominent success message
      toast.success('🎉 Deine Anfrage wurde erfolgreich versendet!', {
        duration: 6000,
        style: {
          background: '#10b981',
          color: '#fff',
          fontSize: '18px',
          fontWeight: 'bold',
          padding: '20px',
          borderRadius: '12px',
        },
      });
      setShowDialog(false);
      setRequestForm({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        event_type: '',
        event_date: '',
        event_location: '',
        message: ''
      });
    } catch (error) {
      console.error('Request error:', error);
      // Fehlermeldung des Servers durchreichen. FastAPI liefert bei
      // Validierungsfehlern (422) ein Array unter `detail`, bei bewussten
      // Abweisungen (403/404/409) einen String.
      const detail = error.response?.data?.detail;
      let msg = 'Fehler beim Senden der Anfrage';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        msg = detail[0].msg;
      }
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % provider.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + provider.images.length) % provider.images.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Lädt...</div>
      </div>
    );
  }

  // Mock data for demo (in production, this comes from backend)
  const mockStats = {
    successfulEvents: 250
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="pt-20">
        {/* Hero Section with Image Gallery */}
        <div className="bg-white border-b">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <button
              onClick={() => navigate('/search')}
              className="flex items-center gap-2 text-gray-600 hover:text-[#2B2142] mb-4 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Zurück zur Suche
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
              {/* Image Gallery */}
              <div className="w-full">
                {provider.images && provider.images.length > 0 ? (
                  <div className="relative w-full">
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-gray-100">
                      <img
                        src={getImageUrl(provider.images[currentImageIndex])}
                        alt={provider.company_name}
                        className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                        onClick={() => setSelectedImage(currentImageIndex)}
                        onError={(e) => {
                          console.error('Image load error:', provider.images[currentImageIndex]);
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f3f4f6" width="400" height="300"/%3E%3Ctext fill="%239ca3af" x="50%25" y="50%25" text-anchor="middle" dy=".3em" font-size="20"%3EBild nicht verfügbar%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                    
                    {provider.images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-colors z-10"
                        >
                          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-colors z-10"
                        >
                          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>
                        
                        <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 sm:gap-2">
                          {provider.images.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={() => setCurrentImageIndex(idx)}
                              className={`w-2 h-2 rounded-full transition-all ${
                                idx === currentImageIndex ? 'bg-white w-8' : 'bg-white/60'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="aspect-[4/3] rounded-2xl bg-gradient-to-br from-[#FFF0D1] to-[#F4F2F2] flex items-center justify-center">
                    <p className="text-6xl font-bold text-gray-300">
                      {provider.company_name?.charAt(0) || '?'}
                    </p>
                  </div>
                )}

                {/* Thumbnail Gallery */}
                {provider.images && provider.images.length > 1 && (
                  <div className="grid grid-cols-4 gap-2 mt-4">
                    {provider.images.slice(0, 4).map((img, idx) => (
                      <div
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                          idx === currentImageIndex ? 'border-[#2B2142] ring-2 ring-[#2B2142]/20' : 'border-transparent hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={getImageUrl(img)}
                          alt={`Thumbnail ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Video Section */}
                {provider.videos && provider.videos.length > 0 && (
                  <div className="mt-4">
                    {provider.videos.map((videoSource, idx) => (
                      <video
                        key={idx}
                        src={getImageUrl(videoSource)}
                        controls
                        className="w-full rounded-2xl bg-black"
                        onError={(e) => {
                          console.error('Video load error:', videoSource);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Provider Info & CTA */}
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 break-words">{provider.company_name}</h1>
                      {provider.plan_level === 'pro' && (
                        <span className="whitespace-nowrap px-2 py-1 sm:px-4 sm:py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs sm:text-sm font-bold rounded-full shadow-lg">
                          🏆 Top-Anbieter
                        </span>
                      )}
                      {provider.plan_level === 'plus' && (
                        <span className="whitespace-nowrap px-2 py-1 sm:px-4 sm:py-2 bg-gradient-to-r from-[#2B2142] to-[#3D3158] text-white text-xs sm:text-sm font-bold rounded-full shadow-lg">
                          ⭐ Empfohlen
                        </span>
                      )}
                      <ShareButton
                        providerName={provider.company_name || provider.name}
                        providerId={provider.id}
                        providerSlug={provider.slug}
                      />
                    </div>
                    <div className="flex gap-2 flex-wrap mb-3">
                      {provider.categories?.slice(0, 3).map((cat) => (
                        <span key={cat} className="px-3 py-1 bg-[#FFF0D1]/30 text-[#2B2142] rounded-lg text-sm font-semibold">
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                  <FavoriteButton
                    providerId={provider.id}
                    size="large"
                    showCount={false}
                  />
                </div>

                <div className="flex items-center gap-2 text-gray-600 mb-6">
                  <MapPin className="w-5 h-5" />
                  <span className="text-lg">{provider.location_city}, {provider.location_zip}</span>
                </div>

                {/* Trust Badge - Rating */}
                <div className="mb-6">
                  <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-4 rounded-xl border border-yellow-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Star className="w-5 h-5 text-yellow-500 fill-current" />
                      <span className="text-2xl font-bold text-gray-900">
                        {reviewStats.average_rating > 0 ? reviewStats.average_rating : 'Neu'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {reviewStats.total_reviews > 0
                        ? `${reviewStats.total_reviews} Bewertung${reviewStats.total_reviews !== 1 ? 'en' : ''}`
                        : 'Noch keine Bewertungen'}
                    </p>
                  </div>
                </div>

                {/* Quick Facts */}
                <div className="space-y-3 mb-6 p-4 bg-gray-50 rounded-xl">
                  {provider.years_of_experience && (
                    <div className="flex items-center gap-3">
                      <Award className="w-5 h-5 text-gray-600" />
                      <span className="text-sm text-gray-700">{provider.years_of_experience} Jahre Erfahrung</span>
                      <CheckCircle className="w-4 h-4 text-green-600 ml-auto" />
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-gray-600" />
                    <span className="text-sm text-gray-700">Verifizierter Dienstleister</span>
                    <CheckCircle className="w-4 h-4 text-green-600 ml-auto" />
                  </div>
                  {provider.availability_hint && (provider.plan_level === 'plus' || provider.plan_level === 'pro') && (
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-gray-600" />
                      <span className="text-sm text-gray-700 break-all min-w-0">{provider.availability_hint}</span>
                      <CheckCircle className="w-4 h-4 text-green-600 ml-auto" />
                    </div>
                  )}
                </div>

                {/* Price Range */}
                {provider.base_price_from && (
                  <div className="mb-6 p-4 bg-gradient-to-br from-[#FFF0D1]/30 to-[#F4F2F2] rounded-xl border border-[#2B2142]/20">
                    <p className="text-sm text-gray-600 mb-1">Preisrahmen</p>
                    <p className="text-2xl font-bold text-[#2B2142]">{provider.base_price_from}</p>
                  </div>
                )}

                <button
                  onClick={openInquiry}
                  className="w-full py-4 bg-[#2B2142] hover:bg-[#1E1730] text-white text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  Jetzt unverbindlich anfragen
                </button>

                <Dialog open={showDialog} onOpenChange={setShowDialog}>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-bold">Anfrage senden an {provider.company_name}</DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleSendRequest} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Dein Name *</label>
                          <input
                            type="text"
                            required
                            value={requestForm.customer_name}
                            onChange={(e) => setRequestForm({ ...requestForm, customer_name: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Deine E-Mail</label>
                          <input
                            type="email"
                            readOnly
                            value={user?.email || ''}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-600 focus:outline-none"
                          />
                          <p className="mt-1 text-xs text-gray-500">Aus deinem Konto uebernommen</p>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Deine Telefonnummer (optional)</label>
                        <input
                          type="tel"
                          inputMode="tel"
                          maxLength={20}
                          pattern="[+\d\s\-()]*"
                          value={requestForm.customer_phone}
                          onChange={(e) => setRequestForm({ ...requestForm, customer_phone: e.target.value.replace(/[^\d+\s\-()]/g, '') })}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                          placeholder="z.B. +49 170 1234567"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Art des Events *</label>
                        <input
                          type="text"
                          required
                          value={requestForm.event_type}
                          onChange={(e) => setRequestForm({ ...requestForm, event_type: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                          placeholder="z.B. Hochzeit, Geburtstag, Firmenevent"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Datum (optional)</label>
                          <input
                            type="date"
                            value={requestForm.event_date}
                            onChange={(e) => setRequestForm({ ...requestForm, event_date: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Ort *</label>
                          <input
                            type="text"
                            required
                            value={requestForm.event_location}
                            onChange={(e) => setRequestForm({ ...requestForm, event_location: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                            placeholder="Stadt oder genaue Adresse"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Deine Nachricht *</label>
                        <textarea
                          required
                          minLength={10}
                          value={requestForm.message}
                          onChange={(e) => setRequestForm({ ...requestForm, message: e.target.value })}
                          rows={5}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                          placeholder="Beschreibe dein Event und deine Wünsche..."
                        />
                        <p className="mt-1 text-xs text-gray-500">Mindestens 10 Zeichen.</p>
                      </div>
                      
                      <button
                        type="submit"
                        disabled={sending}
                        className="w-full py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl disabled:opacity-50 transition-colors"
                      >
                        {sending ? 'Wird gesendet...' : 'Anfrage senden'}
                      </button>
                    </form>
                  </DialogContent>
                </Dialog>

                <p className="text-center text-sm text-gray-500 mt-3">
                  ✓ Kostenlos & unverbindlich
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8 min-w-0">
              {/* About Section */}
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Über uns</h2>
                <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-line break-words">
                  {provider.long_description || provider.short_description}
                </p>
              </div>

              {/* Services/Categories */}
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Unsere Leistungen</h2>
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  {provider.categories?.map((cat) => (
                    <div key={cat} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-[#FFF0D1]/30 rounded-lg min-w-0">
                      <CheckCircle className="w-5 h-5 text-[#2B2142] flex-shrink-0" />
                      <span className="text-gray-900 font-medium text-xs sm:text-sm md:text-base">{cat}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reviews Section */}
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Bewertungen</h2>
                  {reviewStats.total_reviews > 0 && (
                    <div className="flex items-center gap-2">
                      <Star className="w-6 h-6 text-yellow-500 fill-current" />
                      <span className="text-xl font-bold">{reviewStats.average_rating}</span>
                      <span className="text-gray-600">({reviewStats.total_reviews})</span>
                    </div>
                  )}
                </div>
                {reviewsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
                  </div>
                ) : reviews.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">
                    Noch keine Bewertungen. Sei der Erste, der eine Bewertung schreibt!
                  </p>
                ) : (
                  <div className="space-y-6">
                    {reviews.map((review) => (
                      <div key={review.id} className="border-b border-gray-100 pb-6 last:border-0 last:pb-0">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-semibold text-gray-900">{review.customer_name || 'Kunde'}</p>
                            <p className="text-sm text-gray-500">
                              {review.created_at ? new Date(review.created_at).toLocaleDateString('de-DE', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              }) : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-5 h-5 ${i < review.rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}`}
                              />
                            ))}
                          </div>
                        </div>
                        {review.title && (
                          <h4 className="font-medium text-gray-900 mb-2 break-words">{review.title}</h4>
                        )}
                        <p className="text-gray-600 break-words">{review.comment}</p>
                        {review.event_type && (
                          <p className="text-sm text-gray-500 mt-2 break-words">
                            Veranstaltung: {review.event_type}
                          </p>
                        )}
                        {review.provider_response && (
                          <div className="mt-4 bg-gray-50 rounded-lg p-4">
                            <p className="text-sm font-medium text-gray-700 mb-1">Antwort des Anbieters:</p>
                            <p className="text-sm text-gray-600 break-words">{review.provider_response}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Contact Info */}
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-4">Kontakt & Links</h3>
                  <div className="space-y-3">
                    {provider.email && (
                      <a
                        href={`mailto:${provider.email}`}
                        className="flex items-center gap-3 p-3 bg-[#FFF0D1]/30 hover:bg-[#FFF0D1] rounded-lg transition-colors"
                      >
                        <Mail className="w-5 h-5 text-[#2B2142]" />
                        <span className="text-gray-900 font-medium">{provider.email}</span>
                      </a>
                    )}
                    {provider.instagram_link && (
                      <a
                        href={provider.instagram_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 rounded-lg transition-colors"
                      >
                        <Instagram className="w-5 h-5 text-purple-600" />
                        <span className="text-gray-900 font-medium">Instagram</span>
                        <ExternalLink className="w-4 h-4 ml-auto text-gray-400" />
                      </a>
                    )}
                    {provider.website_link && (
                      <a
                        href={provider.website_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Globe className="w-5 h-5 text-gray-700" />
                        <span className="text-gray-900 font-medium">Website</span>
                        <ExternalLink className="w-4 h-4 ml-auto text-gray-400" />
                      </a>
                    )}
                  </div>
                </div>

                {/* Trust Badge */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-200">
                  <div className="flex items-center gap-3 mb-3">
                    <Award className="w-8 h-8 text-green-600" />
                    <h3 className="font-bold text-gray-900">Verifiziert</h3>
                  </div>
                  <p className="text-sm text-gray-700">
                    Dieser Dienstleister wurde von EventConnect geprüft und verifiziert.
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#2B2142]">
                  <button
                    onClick={openInquiry}
                    className="w-full py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-bold rounded-xl transition-colors"
                  >
                    Anfrage senden
                  </button>
                  <p className="text-center text-xs text-gray-500 mt-2">
                    Kostenlos & unverbindlich
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Plus/Pro Features */}
      {(provider.plan_level === 'plus' || provider.plan_level === 'pro') && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
          
          {/* Packages Section (Plus+) */}
          {provider.packages && provider.packages.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Unsere Angebotspakete</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {provider.packages.map((pkg, index) => (
                  <div key={index} className="border-2 border-gray-200 rounded-xl p-4 sm:p-6 hover:border-[#2B2142] transition-colors min-w-0 overflow-hidden">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 break-words">{pkg.name}</h3>
                    <p className="text-2xl font-bold text-[#2B2142] mb-4">{pkg.price_from}</p>
                    <p className="text-gray-700 mb-4 break-words">{pkg.description}</p>
                    {pkg.features && pkg.features.length > 0 && (
                      <ul className="space-y-2">
                        {pkg.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                            <span className="text-gray-700 break-words min-w-0">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Specializations (Plus+) */}
          {provider.specializations && provider.specializations.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Spezialisierungen</h2>
              <div className="flex flex-wrap gap-3">
                {provider.specializations.map((spec, index) => (
                  <span key={index} className="px-4 py-2 bg-[#FFF0D1] text-[#2B2142] rounded-full font-semibold">
                    {spec}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* B2B Section (Pro only) */}
          {provider.plan_level === 'pro' && provider.b2b_focus && (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-lg p-4 sm:p-8 border-2 border-purple-200 overflow-hidden">
              <div className="flex items-center gap-3 mb-6">
                <Trophy className="w-10 h-10 text-purple-600" />
                <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Perfekt für Firmen & große Events</h2>
              </div>
              
              {provider.b2b_description && (
                <p className="text-gray-700 mb-6 text-lg break-words">{provider.b2b_description}</p>
              )}

              <div className="grid md:grid-cols-2 gap-6 min-w-0">
                {provider.b2b_event_types && provider.b2b_event_types.length > 0 && (
                  <div className="min-w-0 overflow-hidden">
                    <h3 className="font-bold text-gray-900 mb-3">B2B Event-Typen</h3>
                    <div className="flex flex-wrap gap-2">
                      {provider.b2b_event_types.map((type, index) => (
                        <span key={index} className="px-3 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold break-words">
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {(provider.capacity_range || provider.travel_scope || provider.team_size_range) && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3">Kapazitäten</h3>
                    <ul className="space-y-2">
                      {provider.capacity_range && (
                        <li className="flex items-start gap-2">
                          <Users className="w-5 h-5 text-purple-600 flex-shrink-0" />
                          <span className="text-gray-700">Eventgröße: {provider.capacity_range}</span>
                        </li>
                      )}
                      {provider.team_size_range && (
                        <li className="flex items-start gap-2">
                          <Users className="w-5 h-5 text-purple-600 flex-shrink-0" />
                          <span className="text-gray-700">Teamgröße: {provider.team_size_range}</span>
                        </li>
                      )}
                      {provider.travel_scope && (
                        <li className="flex items-start gap-2">
                          <MapPin className="w-5 h-5 text-purple-600 flex-shrink-0" />
                          <span className="text-gray-700">Reisebereitschaft: {provider.travel_scope}</span>
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {provider.reference_clients && provider.reference_clients.length > 0 && (
                  <div className="md:col-span-2 min-w-0 overflow-hidden">
                    <h3 className="font-bold text-gray-900 mb-3">Referenzkunden</h3>
                    <div className="flex flex-wrap gap-3">
                      {provider.reference_clients.map((client, index) => (
                        <span key={index} className="px-4 py-2 bg-white border-2 border-purple-200 text-purple-900 rounded-lg font-semibold break-words">
                          {client}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {(provider.has_liability_insurance || provider.has_vat_id || provider.company_legal_form || provider.b2b_invoice_hint) && (
                  <div className="md:col-span-2 bg-white rounded-xl p-4 border-2 border-purple-200 min-w-0">
                    <h3 className="font-bold text-gray-900 mb-3">Unternehmensdaten</h3>
                    <div className="flex flex-wrap gap-4">
                      {provider.has_liability_insurance && (
                        <div className="flex items-center gap-2 min-w-0">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700 break-words min-w-0">Haftpflichtversicherung</span>
                        </div>
                      )}
                      {provider.has_vat_id && (
                        <div className="flex items-center gap-2 min-w-0">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700 break-words min-w-0">USt-ID vorhanden</span>
                        </div>
                      )}
                      {provider.company_legal_form && (
                        <div className="flex items-center gap-2 min-w-0">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700 break-words min-w-0">{provider.company_legal_form}</span>
                        </div>
                      )}
                      {provider.b2b_invoice_hint && (
                        <div className="flex items-center gap-2 min-w-0">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700 break-words min-w-0">{provider.b2b_invoice_hint}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Languages & Certifications (Plus+) */}
          {(provider.languages?.length > 0 || provider.certifications?.length > 0) && (
            <div className="grid md:grid-cols-2 gap-6">
              {provider.languages && provider.languages.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Sprachen</h3>
                  <div className="flex flex-wrap gap-2">
                    {provider.languages.map((lang, index) => (
                      <span key={index} className="px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {provider.certifications && provider.certifications.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Zertifikate</h3>
                  <ul className="space-y-2">
                    {provider.certifications.filter(cert => cert).map((cert, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Award className="w-5 h-5 text-purple-500 flex-shrink-0" />
                        <span className="text-gray-700">{cert}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Top References - Linked Providers */}
          {providerReferences.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Top-Referenzen</h3>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {providerReferences.map((ref) => (
                  <div
                    key={ref.id}
                    onClick={() => navigate(`/provider/${ref.public_id || ref.id}`)}
                    className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-4 cursor-pointer hover:shadow-md transition-all border border-yellow-100 hover:border-yellow-300"
                  >
                    <div className="flex items-start gap-3">
                      {(ref.profile_picture || (ref.images && ref.images.length > 0)) ? (
                        <img
                          src={getImageUrl(ref.profile_picture || ref.images[0])}
                          alt={ref.company_name}
                          className="w-12 h-12 rounded-lg object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-yellow-200 flex items-center justify-center">
                          <span className="text-yellow-700 font-bold text-lg">
                            {ref.company_name?.charAt(0) || '?'}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 mb-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                          <h4 className="font-bold text-gray-900 text-sm truncate">{ref.company_name}</h4>
                        </div>
                        {ref.categories && ref.categories.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {ref.categories.map((cat, idx) => (
                              <span key={idx} className="px-1.5 py-0.5 bg-yellow-50 text-yellow-700 rounded text-xs font-medium">
                                {cat}
                              </span>
                            ))}
                          </div>
                        )}
                        {ref.location_city && (
                          <div className="flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-500">{ref.location_city}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Suggested Providers Section - includes partners first, then others by plan level */}
      {suggestedProviders && suggestedProviders.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Das könnte dir auch gefallen</h2>
            <p className="text-gray-600">
              Entdecke Partner und weitere Dienstleister, die zu deinem Event passen könnten.
            </p>
          </div>

          <div className="px-12">
            <Carousel opts={{ align: "start", loop: false }}>
              <CarouselContent className="-ml-4">
                {suggestedProviders.map((provider) => (
                  <CarouselItem key={provider.id} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                    <div className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-gray-100 hover:border-[#2B2142]/20 group h-full">
                      {/* Image */}
                      <div className="relative h-48 bg-gradient-to-br from-[#FFF0D1] to-[#F4F2F2] overflow-hidden">
                        {(() => {
                          // Use profile_picture if available, otherwise first gallery image
                          const displayImage = provider.profile_picture || (provider.images && provider.images.length > 0 ? provider.images[0] : null);
                          const isProfilePicture = provider.profile_picture && displayImage === provider.profile_picture;

                          return displayImage ? (
                            <img
                              src={getImageUrl(displayImage)}
                              alt={provider.company_name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <div className="text-6xl font-bold text-[#2B2142]/40">
                                {provider.company_name?.charAt(0) || '?'}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Partner Badge */}
                        {provider.is_partner && (
                          <div className="absolute top-3 left-3">
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold rounded-full">
                              Partner
                            </span>
                          </div>
                        )}

                        {/* Plan Badge */}
                        {provider.plan_level === 'pro' && (
                          <div className="absolute top-3 right-3">
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold rounded-full">
                              <Trophy className="w-3 h-3" />
                              Top-Anbieter
                            </span>
                          </div>
                        )}
                        {provider.plan_level === 'plus' && (
                          <div className="absolute top-3 right-3">
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-[#2B2142] to-[#3D3158] text-white text-xs font-bold rounded-full">
                              <Award className="w-3 h-3" />
                              Empfohlen
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-4 md:p-5">
                        {/* Company Name */}
                        <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-1 group-hover:text-[#2B2142] transition-colors">
                          {provider.company_name}
                        </h3>

                        {/* Categories */}
                        {provider.categories && provider.categories.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {provider.categories.slice(0, 2).map((cat, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-[#FFF0D1]/30 text-[#2B2142] rounded-lg text-xs font-semibold"
                              >
                                {cat}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Location */}
                        {provider.location_city && (
                          <div className="flex items-center gap-1 text-sm text-gray-600 mb-3">
                            <MapPin className="w-4 h-4" />
                            <span>{provider.location_city}</span>
                          </div>
                        )}

                        {/* Rating */}
                        {provider.average_rating > 0 && (
                          <div className="flex items-center gap-1 mb-4">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            <span className="font-semibold text-gray-900">
                              {provider.average_rating.toFixed(1)}
                            </span>
                            {provider.review_count > 0 && (
                              <span className="text-sm text-gray-500">
                                ({provider.review_count})
                              </span>
                            )}
                          </div>
                        )}

                        {/* Buttons */}
                        <div className="space-y-2">
                          <button
                            onClick={() => navigate(`/provider/${provider.public_id || provider.id}`)}
                            className="w-full px-4 py-2.5 bg-gradient-to-r from-[#2B2142] to-[#3D3158] hover:from-[#1E1730] hover:to-[#2B2142] text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
                          >
                            Profil ansehen
                          </button>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      {selectedImage !== null && provider.images && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={getImageUrl(provider.images[selectedImage])}
            alt="Full size"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default ProviderDetailPage;
